import { useState, useRef, useEffect } from "react";
import { X, Loader2 } from 'lucide-react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Location05Icon } from '@hugeicons/core-free-icons';
import { getToken } from "@/lib/token-storage";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";

interface CitySuggestion {
  place_id: string;
  description: string;
  main_text: string;
  country_code?: string;
}

// Fallback coordinates for major cities
const CITY_COORDINATES: Record<string, { lat: number; lng: number }> = {
  // Italian cities
  Roma: { lat: 41.9028, lng: 12.4964 },
  Milano: { lat: 45.4642, lng: 9.1900 },
  Napoli: { lat: 40.8518, lng: 14.2681 },
  Torino: { lat: 45.0703, lng: 7.6869 },
  Bologna: { lat: 44.4949, lng: 11.3426 },
  Firenze: { lat: 43.7696, lng: 11.2558 },
  Venezia: { lat: 45.4408, lng: 12.3155 },
  Palermo: { lat: 38.1157, lng: 13.3615 },
  Genova: { lat: 44.4056, lng: 8.9463 },
  Bari: { lat: 41.1171, lng: 16.8719 },
  Catania: { lat: 37.5079, lng: 15.0830 },
  Verona: { lat: 45.4384, lng: 10.9916 },
  Padova: { lat: 45.4064, lng: 11.8768 },
  Trieste: { lat: 45.6495, lng: 13.7768 },
  Brescia: { lat: 45.5416, lng: 10.2118 },
  Parma: { lat: 44.8015, lng: 10.3279 },
  Modena: { lat: 44.6471, lng: 10.9254 },
  Cagliari: { lat: 39.2238, lng: 9.1217 },
  // European cities
  Berlin: { lat: 52.5200, lng: 13.4050 },
  Paris: { lat: 48.8566, lng: 2.3522 },
  Madrid: { lat: 40.4168, lng: -3.7038 },
  Barcelona: { lat: 41.3851, lng: 2.1734 },
  London: { lat: 51.5074, lng: -0.1278 },
  Amsterdam: { lat: 52.3676, lng: 4.9041 },
  Vienna: { lat: 48.2082, lng: 16.3738 },
  Zurich: { lat: 47.3769, lng: 8.5417 },
  Munich: { lat: 48.1351, lng: 11.5820 },
};

function getFallbackCoordinates(cityName: string): { lat: number; lng: number } | null {
  const normalized = cityName.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  
  for (const [key, coords] of Object.entries(CITY_COORDINATES)) {
    if (key.toLowerCase() === normalized || key.toLowerCase() === cityName.toLowerCase()) {
      return coords;
    }
  }
  for (const [key, coords] of Object.entries(CITY_COORDINATES)) {
    if (normalized.includes(key.toLowerCase()) || key.toLowerCase().includes(normalized)) {
      return coords;
    }
  }
  return null;
}

function countryCodeToFlag(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return "";
  const codePoints = countryCode.toUpperCase().split("").map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function extractCountryCode(description: string): string {
  const parts = description.split(",");
  if (parts.length > 0) {
    const country = parts[parts.length - 1]?.trim();
    const countryToCode: Record<string, string> = {
      Italy: "IT", Turchia: "TR", Turkey: "TR", "Türkiye": "TR",
      Germany: "DE", Deutschland: "DE",
      France: "FR", Francia: "FR",
      Spain: "ES", España: "ES", Spagna: "ES",
      "United Kingdom": "GB", UK: "GB",
      England: "GB", Scotland: "GB", Wales: "GB",
      Netherlands: "NL", Holland: "NL",
      Belgium: "BE", België: "BE", Belgique: "BE",
      Switzerland: "CH", Suisse: "CH",
      Austria: "AT", Österreich: "AT",
      Portugal: "PT", Portogallo: "PT",
      Poland: "PL", Polska: "PL",
      "Czech Republic": "CZ", "Czechia": "CZ",
      Hungary: "HU", Magyarország: "HU",
      Greece: "GR", Ελλάδα: "GR",
      Sweden: "SE", Sverige: "SE",
      Norway: "NO", Norge: "NO",
      Denmark: "DK", Danmark: "DK",
      Finland: "FI", Suomi: "FI",
      Ireland: "IE", Éire: "IE",
      Monaco: "MC",
      Andorra: "AD",
      "San Marino": "SM",
      "Vatican City": "VA",
      Malta: "MT",
      Slovakia: "SK", Slovensko: "SK",
      Slovenia: "SI", Slovenija: "SI",
      Croatia: "HR",
      Serbia: "RS",
      Montenegro: "ME",
      Albania: "AL",
      Bulgaria: "BG",
      Romania: "RO",
      Ukraine: "UA",
      "United States": "US", USA: "US",
      Canada: "CA",
      Australia: "AU",
      Brazil: "BR",
      Japan: "JP",
      China: "CN",
      Russia: "RU",
    };
    return countryToCode[country] || "";
  }
  return "";
}

function extractState(description: string): string {
  const parts = description.split(",").map(p => p.trim());
  return parts[parts.length - 1] || "";
}

interface SimpleCitySelectorProps {
  onCitySelected?: (city: string, latitude: string, longitude: string) => void;
  placeholder?: string;
  defaultCity?: string;
  className?: string;
  registrationMode?: boolean;
  autoFillUserCity?: boolean; // Se true, precompila con la città dell'utente
  /** 'mobile' (default): rounded-full pill on #252525 — original mobile header look.
   *  'desktop': rectangular rounded-10 on KG.panel with KG.border — matches DesktopTopBar buttons. */
  variant?: 'mobile' | 'desktop';
}

export function SimpleCitySelector({
  onCitySelected,
  placeholder = "Cerca una città...",
  defaultCity = "",
  className = "",
  registrationMode = false,
  autoFillUserCity = true, // Default true per mantenere comportamento attuale
  variant = 'mobile',
}: SimpleCitySelectorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, setUser } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  // Ref per tracciare quando stiamo cambiando manualmente la città
  const isManuallyChangingRef = useRef(false);

  // Se autoFillUserCity è false, usa solo defaultCity
  const initialCity = autoFillUserCity ? (defaultCity || (user?.city as string) || "") : defaultCity;

  const [searchQuery, setSearchQuery] = useState(initialCity);
  const [selectedCity, setSelectedCity] = useState(initialCity);

  // Debug: log ogni render
  console.log('[SimpleCitySelector] 🔄 RENDER - selectedCity:', selectedCity, 'user?.city:', user?.city, 'defaultCity:', defaultCity, 'isManuallyChanging:', isManuallyChangingRef.current);
  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // NON resettare se stiamo cambiando manualmente la città
    if (isManuallyChangingRef.current) {
      console.log('[SimpleCitySelector] 🚫 Skipping auto-update because manual change in progress');
      return;
    }
    // Solo aggiorna automaticamente se autoFillUserCity è true
    if (autoFillUserCity && user?.city && user?.city !== selectedCity) {
      console.log('[SimpleCitySelector] 🔄 Auto-updating selectedCity from user?.city:', user?.city);
      setSelectedCity(user.city as string);
    }
  }, [user?.city, selectedCity, autoFillUserCity]);

  // Focus tracking — when the input is not focused we render `selectedCity` as the
  // value so the city is always visible (not just as the browser-dimmed placeholder).
  // This avoids the desktop edge case where the auth context resolves after mount and
  // the initial useState fell back to "".
  const [isFocused, setIsFocused] = useState(false);

  // Initialize with default city coordinates if provided
  useEffect(() => {
    if (initialCity && onCitySelected) {
      const fallbackCoords = getFallbackCoordinates(initialCity);
      if (fallbackCoords) {
        console.log('[SimpleCitySelector] 🎯 Initializing with default city:', initialCity, fallbackCoords);
        onCitySelected(initialCity, fallbackCoords.lat.toString(), fallbackCoords.lng.toString());
      }
    }
  }, []); // Solo al mount iniziale

  // Listen for city change events (from TravelQuarterContainer, SpeedDateQuarterContainer, etc.)
  useEffect(() => {
    const handleCityChanged = (e: Event) => {
      const customEvent = e as CustomEvent<{ city: string; latitude?: string; longitude?: string }>;
      const { city: newCity, latitude, longitude } = customEvent.detail || {};
      if (newCity) {
        console.log('[SimpleCitySelector] 🏙️ City changed via event:', newCity);
        // Set flag to prevent useEffect from resetting selectedCity back to old user?.city
        isManuallyChangingRef.current = true;
        setSelectedCity(newCity);
        // Clear searchQuery so the placeholder (selectedCity) becomes visible
        setSearchQuery("");
        setSuggestions([]);
        setShowDropdown(false);

        // Update the auth context so the city persists across page navigation
        if (user && setUser && latitude && longitude) {
          setUser({
            ...user,
            city: newCity,
            latitude,
            longitude
          });
          console.log('[SimpleCitySelector] ✅ Auth context updated with new city:', newCity);
        }

        setTimeout(() => {
          isManuallyChangingRef.current = false;
        }, 2000);
      }
    };

    document.addEventListener('cityChanged', handleCityChanged);
    return () => {
      document.removeEventListener('cityChanged', handleCityChanged);
    };
  }, [user, setUser]);

  // Simple synchronous function to update state
  const handleCitySelected = (name: string, lat: number, lng: number) => {
    console.log('[SimpleCitySelector] 🎯 handleCitySelected:', { name, lat, lng });
    console.log('[SimpleCitySelector] 🔍 Current state BEFORE update:', { selectedCity, searchQuery });

    try {
      // IMPORTANTE: Imposta il flag PRIMA di cambiare lo stato
      // per evitare che l'useEffect resetti il valore
      isManuallyChangingRef.current = true;
      console.log('[SimpleCitySelector] 🚩 Set isManuallyChangingRef = true');

      // Dispatch event with city AND coordinates so listeners have complete info
      const cityChangedEvent = new CustomEvent('cityChanged', {
        detail: { city: name, latitude: lat.toString(), longitude: lng.toString() }
      });
      document.dispatchEvent(cityChangedEvent);
      console.log('[SimpleCitySelector] 🏙️ Dispatched cityChanged event:', { city: name, lat, lng });

      // Then update state
      setSearchQuery("");
      setSuggestions([]);
      setShowDropdown(false);
      setSelectedCity(name);
      console.log('[SimpleCitySelector] ✅ setSelectedCity called with:', name);

      // Resetta il flag dopo un delay per permettere a user?.city di aggiornarsi
      setTimeout(() => {
        isManuallyChangingRef.current = false;
        console.log('[SimpleCitySelector] 🚩 Reset isManuallyChangingRef = false');
      }, 2000);

      if (onCitySelected) {
        console.log('[SimpleCitySelector] 📞 Calling onCitySelected callback...');
        onCitySelected(name, lat.toString(), lng.toString());
        console.log('[SimpleCitySelector] ✅ onCitySelected callback completed');
      }
    } catch (error) {
      console.error('[SimpleCitySelector] ❌ Error in handleCitySelected:', error);
    }
  };

  // Handle city selection with API calls
  const handleSelectCity = async (placeId: string, cityName: string) => {
    try {
      console.log('[SimpleCitySelector] 🎯 handleSelectCity called:', { placeId, cityName, registrationMode });

      let lat = 41.9028, lng = 12.4964;
      let useFallback = false;

      // Get coordinates
      try {
        console.log('[SimpleCitySelector] 📡 Fetching coordinates...');
        const detailsResponse = await fetch(`/api/places/details?place_id=${placeId}&fields=geometry`);
        if (detailsResponse.ok) {
          const detailsData = await detailsResponse.json();
          if (detailsData.status === "OK" && detailsData.result?.geometry?.location) {
            lat = detailsData.result.geometry.location.lat;
            lng = detailsData.result.geometry.location.lng;
            console.log('[SimpleCitySelector] ✅ Got coordinates:', { lat, lng });
          } else {
            console.log('[SimpleCitySelector] ⚠️ Invalid geometry, using fallback');
            useFallback = true;
          }
        } else {
          console.log('[SimpleCitySelector] ⚠️ Fetch failed, using fallback');
          useFallback = true;
        }
      } catch (coordError) {
        console.error('[SimpleCitySelector] ❌ Error fetching coordinates:', coordError);
        useFallback = true;
      }

      if (useFallback) {
        const fallbackCoords = getFallbackCoordinates(cityName);
        if (fallbackCoords) {
          lat = fallbackCoords.lat;
          lng = fallbackCoords.lng;
          console.log('[SimpleCitySelector] 📍 Using fallback coordinates:', { lat, lng });
        }
      }

      // Update user city if not in registration mode
      if (!registrationMode) {
        try {
          console.log('[SimpleCitySelector] 💾 Updating user city in DB...');
          const updateResponse = await fetch('/api/users/update', {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getToken()}`,
            },
            body: JSON.stringify({
              city: cityName,
              latitude: lat.toString(),
              longitude: lng.toString()
            })
          });

          if (updateResponse.ok) {
            console.log('[SimpleCitySelector] ✅ User city updated');
            // Update user in context immediately
            if (user && setUser) {
              setUser({
                ...user,
                city: cityName,
                latitude: lat.toString(),
                longitude: lng.toString()
              });
              console.log('[SimpleCitySelector] ✅ User context updated with new city');
            }
            // NOTE: Do NOT refetch /api/auth/me here - it would overwrite our setUser() call
            // The context is already updated via setUser() above
            // Only refetch /api/users to get updated user list
            await queryClient.refetchQueries({ queryKey: ['/api/users'] });
            console.log('[SimpleCitySelector] ✅ Users query refetched');
            toast({ title: "Città aggiornata", description: `Nuova città: ${cityName}` });
          }
        } catch (updateError) {
          console.error('[SimpleCitySelector] ❌ Error updating city:', updateError);
          // Continue anyway - non bloccare il flusso
        }
      }

      // Update local state
      console.log('[SimpleCitySelector] 🔄 Calling handleCitySelected...');
      handleCitySelected(cityName, lat, lng);
      console.log('[SimpleCitySelector] ✅ City selection complete!');

    } catch (error) {
      console.error('[SimpleCitySelector] ❌ CRITICAL ERROR in handleSelectCity:', error);
      // Non fare throw - solo logga l'errore per evitare crash
      if (!registrationMode && toast) {
        try {
          toast({
            title: "Errore",
            description: "Impossibile selezionare la città. Riprova.",
            variant: "destructive"
          });
        } catch (toastError) {
          console.error('[SimpleCitySelector] ❌ Error showing toast:', toastError);
        }
      }
    }
  };

  // Handle search input
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    
    if (!value || value.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    
    fetch(`/api/places/autocomplete?input=${encodeURIComponent(value)}&types=(cities)`)
      .then(response => {
        if (response.ok) return response.json();
        throw new Error('Failed to fetch');
      })
      .then(data => {
        if (data.status === "OK" && data.predictions) {
          const newSuggestions = data.predictions.map((p: any) => ({
            place_id: p.place_id,
            description: p.description,
            main_text: p.structured_formatting?.main_text || p.description,
            country_code: extractCountryCode(p.description)
          }));
          setSuggestions(newSuggestions);
          setShowDropdown(true);
        } else {
          setSuggestions([]);
        }
      })
      .catch(error => {
        console.error('[SimpleCitySelector] Errore ricerca città:', error);
        setSuggestions([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Update dropdown position when showing
  const updateDropdownPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 300)
      });
    }
  };

  const isDesktopVariant = variant === 'desktop';
  const wrapperClass = isDesktopVariant
    ? 'relative rounded-[10px] overflow-hidden bg-[#141416] border border-white/[0.08]'
    : 'relative rounded-full overflow-hidden bg-[#252525]';
  const inputClass = isDesktopVariant
    ? 'w-full pl-9 pr-9 h-[38px] rounded-[10px] focus:outline-none text-[13px] text-[#cfcfd3] placeholder:text-[#cfcfd3] bg-transparent'
    : 'w-full pl-10 pr-10 py-1.5 rounded-full focus:outline-none focus:ring-1 focus:ring-blue-500 text-md text-white placeholder:text-white bg-transparent';
  const iconOffset = isDesktopVariant ? 'left-2.5' : 'left-3';
  const clearOffset = isDesktopVariant ? 'right-2.5' : 'right-3';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className={wrapperClass}>
        <div className="relative">
          <HugeiconsIcon icon={Location05Icon} className={`absolute ${iconOffset} top-1/2 transform -translate-y-1/2 w-4 h-4 text-white`} size={16} />
          <input
            ref={inputRef}
            type="text"
            placeholder={selectedCity || placeholder}
            value={isFocused ? searchQuery : (searchQuery || selectedCity || '')}
            onChange={(e) => {
              handleSearchChange(e.target.value);
              updateDropdownPosition();
            }}
            onFocus={() => {
              setIsFocused(true);
              setSearchQuery("");
              updateDropdownPosition();
              if (suggestions.length > 0) setShowDropdown(true);
            }}
            onBlur={() => {
              // Defer so a click inside the dropdown can still register before blur
              setTimeout(() => setIsFocused(false), 100);
            }}
            className={inputClass}
          />
        </div>
        {searchQuery && (
          <button
            onClick={() => { setSearchQuery(""); setSuggestions([]); setShowDropdown(false); }}
            className={`absolute ${clearOffset} top-1/2 transform -translate-y-1/2`}
          >
            <X className="w-4 h-4 text-gray-400 hover:text-gray-200" />
          </button>
        )}
        {isLoading && <Loader2 className={`absolute ${isDesktopVariant ? 'right-9' : 'right-10'} top-1/2 transform -translate-y-1/2 w-4 h-4 animate-spin text-blue-500`} />}
      </div>

      {showDropdown && suggestions.length > 0 && dropdownPosition && (
        <div
          className="fixed z-[9999] bg-[#252525] border border-[#353535] rounded-lg shadow-lg max-h-60 overflow-y-auto"
          style={{
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: dropdownPosition.width
          }}
        >
          {suggestions.map((suggestion) => {
            const flag = countryCodeToFlag(suggestion.country_code || extractCountryCode(suggestion.description));
            const country = extractState(suggestion.description);
            return (
              <button
                key={suggestion.place_id}
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('[SimpleCitySelector] 🖱️ City button clicked:', suggestion.main_text);
                  try {
                    await handleSelectCity(suggestion.place_id, suggestion.main_text);
                  } catch (error) {
                    console.error('[SimpleCitySelector] ❌ Error in button onClick:', error);
                  }
                }}
                className="w-full text-left px-4 py-3 hover:bg-[#2a2a2a] border-b border-[#353535] last:border-b-0 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <HugeiconsIcon icon={Location05Icon} className="w-4 h-4 text-gray-400 flex-shrink-0" size={16} />
                  <span className="font-medium text-sm text-white">{suggestion.main_text}</span>
                  {country && <span className="text-xs text-gray-400 uppercase">{country}</span>}
                  {flag && <span className="text-sm">{flag}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
